import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { buildImageUrl } from "@/lib/api";
import { Surface, PageSection, formatINR } from "@/components/shared";
import StatusBadge from "@/components/StatusBadge";
import { PRODUCT } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import ProductSettingsModal from "@/components/ProductSettingsModal";
import { ArrowLeft, Upload, Trash2, ImagePlus, Star, Loader2, Settings2, Bell } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const res = await api.get(`/products/${id}`);
    setProduct(res.data);
  };

  useEffect(() => { load(); }, [id]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/products/${id}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Image uploaded");
      e.target.value = "";
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (imgId) => {
    try {
      await api.delete(`/products/${id}/images/${imgId}`);
      toast.success("Image removed");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!product) {
    return <div className="h-64 bg-white border border-gray-200 rounded-md pulse-soft" />;
  }

  const images = product.images || [];
  const remaining = 3 - images.length;

  return (
    <div className="space-y-6" data-testid={PRODUCT.detail}>
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Link to="/products" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> Back to products
        </Link>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-500">Wings ID</span>
            <span className="font-mono font-semibold text-gray-900">{product.wings_product_id}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSettingsOpen(true)}
            data-testid="product-detail-settings"
          >
            <Settings2 className="h-3.5 w-3.5 mr-1.5" /> Images & Alerts
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left - Images + specs */}
        <div className="lg:col-span-2 space-y-6">
          <Surface padded={false}>
            <div className="aspect-square bg-gradient-to-br from-amber-50 to-red-50 relative">
              {images[activeImage] ? (
                <img
                  src={buildImageUrl(images[activeImage].storage_path)}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                  <ImagePlus className="h-12 w-12 mb-2" strokeWidth={1.5} />
                  <p className="text-sm">No images uploaded</p>
                  <p className="text-xs mt-1">Wings doesn't store images — upload up to 3 here</p>
                </div>
              )}
            </div>
            <div className="p-3 grid grid-cols-3 gap-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className={`aspect-square rounded-md overflow-hidden border-2 relative group ${
                    activeImage === i && images[i] ? "border-[#9B1C1C]" : "border-gray-200"
                  } ${!images[i] && "border-dashed bg-gray-50"} ${images[i] ? "cursor-pointer" : ""}`}
                  onClick={() => images[i] && setActiveImage(i)}
                  role={images[i] ? "button" : undefined}
                  tabIndex={images[i] ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (images[i] && (e.key === "Enter" || e.key === " ")) {
                      e.preventDefault();
                      setActiveImage(i);
                    }
                  }}
                >
                  {images[i] ? (
                    <>
                      <img src={buildImageUrl(images[i].storage_path)} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); deleteImage(images[i].id); }}
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-white/95 text-red-700 flex items-center justify-center opacity-0 group-hover:opacity-100 shadow"
                        data-testid={PRODUCT.deleteImage(images[i].id)}
                        aria-label="Delete image"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                      <ImagePlus className="h-5 w-5" />
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="px-4 pb-4">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleUpload}
                className="hidden"
                data-testid={PRODUCT.uploadInput}
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading || remaining === 0}
                className="w-full bg-gray-900 hover:bg-gray-800"
                data-testid={PRODUCT.uploadButton}
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><Upload className="h-4 w-4 mr-2" /> Upload Image ({remaining} remaining)</>
                )}
              </Button>
            </div>
          </Surface>

          {/* Specs */}
          <Surface>
            <h3 className="font-heading text-base font-semibold text-gray-900 mb-4">Specifications</h3>
            <dl className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm">
              {Object.entries(product.specs || {}).map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs uppercase tracking-widest text-gray-500 font-semibold">{k.replace(/_/g, " ")}</dt>
                  <dd className="text-gray-900 font-medium mt-0.5">{String(v)}</dd>
                </div>
              ))}
            </dl>
          </Surface>

          {product.variants && product.variants.length > 0 && (
            <Surface>
              <h3 className="font-heading text-base font-semibold text-gray-900 mb-4">Variants</h3>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v, i) => (
                  <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-xs font-medium">
                    <span className="text-gray-500">{v.size}</span>
                    <span className="h-3 w-3 rounded-full border border-gray-300" style={{
                      background: v.color === "Gold" ? "#D4AF37" : v.color === "Rose Gold" ? "#B76E79" : "linear-gradient(135deg, #D4AF37, #B76E79)"
                    }} />
                    <span className="text-gray-900">{v.color}</span>
                  </span>
                ))}
              </div>
            </Surface>
          )}
        </div>

        {/* Right - Detail */}
        <div className="lg:col-span-3 space-y-6">
          <Surface>
            <p className="text-xs uppercase tracking-widest text-[#9B1C1C] font-semibold">{product.category} · {product.subcategory}</p>
            <h1 className="font-heading text-3xl font-bold text-gray-900 tracking-tight mt-1">{product.name}</h1>
            <p className="text-sm text-gray-500 font-mono mt-1">{product.sku}</p>

            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Cost Price</p>
                <p className="font-heading text-xl font-bold text-gray-900 mt-1">{formatINR(product.cost_price)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Sale Price</p>
                <p className="font-heading text-xl font-bold text-amber-700 mt-1">{formatINR(product.sale_price)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">MRP</p>
                <p className="font-heading text-xl font-bold text-gray-900 mt-1">{formatINR(product.mrp)}</p>
              </div>
            </div>
          </Surface>

          {/* Branch stock */}
          <PageSection title="Stock Across Branches" description="Threshold = per-branch reorder level set on this product (falls back to default)">
            <Surface padded={false}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Branch</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Available</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Damaged</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Sold 30d</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      <span className="inline-flex items-center gap-1"><Bell className="h-3 w-3" /> Alert ≤</span>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(product.branch_stock || []).map((s) => {
                    const threshold = product.effective_branch_reorder_levels?.[s.branch_code] ?? product.reorder_level;
                    const status = s.quantity === 0 ? "out" : s.quantity < threshold ? "low" : "ok";
                    return (
                      <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-6 py-3 font-medium text-gray-900">{s.branch_name}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold">{s.quantity}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-600">{s.damaged_quantity}</td>
                        <td className="px-4 py-3 text-right font-mono text-emerald-700">{s.sold_last_30d}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-500">{threshold}</td>
                        <td className="px-6 py-3 text-right">
                          <StatusBadge variant={status === "out" ? "error" : status === "low" ? "warning" : "success"}>
                            {status === "out" ? "Out" : status === "low" ? "Low" : "In Stock"}
                          </StatusBadge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Surface>
          </PageSection>

          {/* Suppliers */}
          <PageSection title="Suppliers" description={`${product.suppliers?.length || 0} supplier${(product.suppliers?.length || 0) !== 1 ? "s" : ""} for this product`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(product.suppliers || []).map((s) => (
                <Surface key={s.id} className="hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-heading font-semibold text-gray-900">{s.name}</h4>
                      <p className="text-xs text-gray-500 mt-0.5">{s.city}, {s.state}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold">
                      <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> {s.rating}
                    </span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500 uppercase tracking-widest font-semibold text-[10px]">GST</p>
                      <p className="font-mono mt-0.5">{s.gst}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 uppercase tracking-widest font-semibold text-[10px]">Contact</p>
                      <p className="mt-0.5">{s.contact_person}</p>
                      <p className="font-mono text-gray-600">{s.phone}</p>
                    </div>
                  </div>
                </Surface>
              ))}
            </div>
          </PageSection>
        </div>
      </div>

      <ProductSettingsModal
        productId={id}
        productName={product.name}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSaved={load}
      />
    </div>
  );
}
